import { FC, useEffect, useMemo, useState } from "react";
import { useCommonFlavorCategories } from "@apiHooks/useCommonFlavorCategories";
import {
    Button,
    cssX,
    includesInLC, markMatch,
    SelectComponent,
    TextInput,
    toggleInArray,
} from "@datassential/platform-ui-lib";
import { FiSearch } from "react-icons/fi";
import './SelectFlavorCategories.scss';
import { minusArrayUnique, uniteArraysUnique } from "@helpers";
import cn from "classnames";
import { FaCheck } from "react-icons/fa";
import { IoIosArrowUp } from "react-icons/io";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";

const Toggle: FC<{
    isChecked: boolean;
    onToggle: () => void;
    isPartial?: boolean;
}> = ({
    isChecked,
    onToggle,
    isPartial = false,
    children,
}) => {
    return (
        <div
            className={cn(
                "SelectFlavorCategoriesToggle",
                isChecked && 'is-checked',
                isPartial && 'is-partial',
            )}
            onClick={onToggle}
        >
            <div className="SelectFlavorCategoriesToggle__icon">
                <FaCheck/>
            </div>
            {children}
        </div>
    );
};

const SelectFlavorCategories: FC<{
    value: number[];
    setValue: (value: number[]) => void;
}> = ({
    value,
    setValue,
}) => {
    const [isOpened, setIsOpened] = useState(false);
    const [expandedCategoriesIds, setExpandedCategoriesIds] = useState<number[]>([]);
    const [search, setSearch] = useState('');
    const { data: categoriesDataRaw } = useCommonFlavorCategories();

    const categoriesData = useMemo(
        () => {
            if (!categoriesDataRaw) return [];

            if (search.trim() === '') return categoriesDataRaw;

            return categoriesDataRaw
                .filter(category => category.subCategories?.some(
                    subCategory => includesInLC(subCategory.name ?? '', search)
                ))
                .map(category => ({
                    ...category,
                    subCategories: category.subCategories?.filter(
                        subCategory => includesInLC(subCategory.name ?? '', search)
                    ),
                }))
        },
        [categoriesDataRaw, search]
    );

    useEffect(() => {
        if (!isOpened) {
            setSearch('');
        }
    }, [isOpened]);

    const thumbnailText = useMemo(
        () => {
            if (value.length === 0) {
                return <div style={{ opacity: 0.8 }}>Categories</div>;
            }

            const selectedCategories = categoriesDataRaw?.filter(category => {
                const subCategoriesIds = category.subCategories?.map(i => i.id!) ?? [];
                const selectedSubCategoriesIds = subCategoriesIds.filter(id => value.includes(id));
                return selectedSubCategoriesIds.length > 0;
            }) ?? [];

            if (selectedCategories.length === 1) {
                const category = selectedCategories[0];
                const subCategoriesCount = (category.subCategories ?? []).length;
                const selectedSubs = category.subCategories?.filter(i => value.includes(i.id!)) ?? [];
                const selectedCount = selectedSubs.length;

                if (selectedCount === 1) {
                    return `${selectedSubs[0].name}`;
                }
                if (selectedCount === subCategoriesCount) {
                    return `${category.name}`;
                }

                return `${category.name} (${selectedCount})`;
            }

            return `${selectedCategories.length} Categories`;
        },
        [value, categoriesDataRaw]
    );

    const isSearchMode = search.trim() !== '';

    return (
        <div className="SelectFlavorCategories">
            <SelectComponent
                isOpened={isOpened}
                setIsOpened={setIsOpened}
                thumbnailText={thumbnailText}
                dropdownProps={{
                    isOpened: isOpened,
                    place: "bottom-start",
                    style: {
                        height: 350,
                        overflowX: "hidden",
                        display: 'block',
                        width: 300,
                        padding: 6,
                    },
                }}
            >
                <CustomBaseScrollbars>
                    <div className="SelectFlavorCategories__dropdown-content">
                        <div className={"SelectFlavorCategories__actions"}>
                            <Button
                                onClick={() => setValue([])}
                                modifiers={['naked']}
                                isDisabled={value.length === 0}
                            >
                                clear all
                            </Button>
                        </div>
                        <div className={cn(
                            cssX("SelectComponent__search"),
                            "SelectFlavorCategories__search"
                        )}>
                            <FiSearch className={cssX("SelectComponent__search-icon")}/>
                            <TextInput
                                placeholder="Search"
                                value={search}
                                setValue={setSearch}
                                autoFocus
                            />
                        </div>
                        <div className="SelectFlavorCategories__options">
                            {categoriesData.map(category => {
                                const rawCategory = categoriesDataRaw?.find(i => i.id === category.id)!;
                                const subCategoriesIds = rawCategory.subCategories?.map(i => i.id!) ?? [];
                                const selectedSubCategoriesIds = subCategoriesIds.filter(id => value.includes(id));
                                const isAllSelected = subCategoriesIds.length === selectedSubCategoriesIds.length;

                                return (
                                    <div
                                        key={category.id}
                                        className={cn(
                                            "SelectFlavorCategories__group",
                                            !isSearchMode && !expandedCategoriesIds.includes(category.id!) && 'is-collapsed',
                                        )}
                                    >
                                        <div className="SelectFlavorCategories__row">
                                            <Toggle
                                                isChecked={selectedSubCategoriesIds.length > 0}
                                                isPartial={!isAllSelected}
                                                onToggle={() => {
                                                    const pipe = isAllSelected
                                                        ? minusArrayUnique
                                                        : uniteArraysUnique
                                                    ;

                                                    setValue(pipe(
                                                        value,
                                                        subCategoriesIds,
                                                    ));
                                                }}
                                            >
                                                <div dangerouslySetInnerHTML={{
                                                    __html: markMatch(category.name ?? '', search.trim()),
                                                }}/>
                                            </Toggle>
                                            {!isSearchMode && (
                                                <IoIosArrowUp
                                                    className="SelectFlavorCategories__group-toggle"
                                                    onClick={() => {
                                                        setExpandedCategoriesIds(toggleInArray(
                                                            expandedCategoriesIds,
                                                            category.id!,
                                                        ))
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className="SelectFlavorCategories__group-options">

                                            {category.subCategories?.map(subCategory => (
                                                <div
                                                    key={subCategory.id}
                                                    className="SelectFlavorCategories__row is-nested"
                                                >
                                                    <Toggle
                                                        isChecked={value.includes(subCategory.id!)}
                                                        onToggle={() => {
                                                            setValue(toggleInArray(
                                                                value,
                                                                subCategory.id!,
                                                            ))
                                                        }}
                                                    >
                                                        <div dangerouslySetInnerHTML={{
                                                            __html: markMatch(subCategory.name ?? '', search.trim()),
                                                        }}/>
                                                    </Toggle>
                                                </div>
                                            ))}

                                        </div>
                                    </div>
                                );
                            })}
                            {search.trim() !== '' && categoriesData.length === 0 && (
                                <div className={cssX("SearchBox__no-results")}>
                                    Nothing found.<br/>Try searching something else.
                                </div>
                            )}
                        </div>
                    </div>
                </CustomBaseScrollbars>
            </SelectComponent>
        </div>
    );
}

export default SelectFlavorCategories;